/**
 * TextBubble component.
 *
 * Renders bot messages (left-aligned, surface background) and
 * user messages (right-aligned, primary color background).
 * Bot messages support markdown formatting via the lightweight parser.
 * User messages are rendered as plain text (no markdown).
 */

import DOMPurify from "dompurify";
import type { ChatMessage } from "../../types";
import { parseMarkdown } from "./markdown";
import { formatTimestamp } from "./timestamp";
import { createStatusIndicator } from "./MessageStatus";
import { safeImageUrl } from "./safeImageUrl";

export interface TextBubbleOptions {
  message: ChatMessage;
}

export class TextBubble {
  private readonly element: HTMLElement;

  constructor(options: TextBubbleOptions) {
    const { message } = options;
    const isUser = message.sender === "user";
    // Issue #497 — a live human agent (Chatwoot takeover) reply. Rendered
    // left-aligned like the bot but with a distinct accent + a "Live agent"
    // attribution label so the patient can tell a human is now replying.
    const isAgent = message.sender === "agent";

    // Wrapper for alignment
    this.element = document.createElement("div");
    this.element.className = isUser
      ? "hana-bubble hana-bubble--user"
      : isAgent
        ? "hana-bubble hana-bubble--agent"
        : "hana-bubble hana-bubble--bot";
    this.element.setAttribute("data-message-id", message.id);

    // Agent attribution label ("Live agent" or "Live agent · Alice"),
    // rendered above the bubble content.
    if (isAgent) {
      const label = document.createElement("div");
      label.className = "hana-bubble__agent-label";
      const name = (message.agentName ?? "").trim();
      label.textContent = name ? `Live agent · ${name}` : "Live agent";
      this.element.appendChild(label);
    }

    // REQ-C-047 — optional specialist photo on a bot message, rendered
    // above the text inside the bubble. Only bot bubbles carry a photo
    // (user/agent messages never have one). The URL is restricted to
    // http(s) via safeImageUrl; on load error the <img> is hidden so the
    // patient sees the text without a broken-image icon.
    if (!isUser && !isAgent) {
      const safe = safeImageUrl(message.photoUrl);
      if (safe) {
        const photo = document.createElement("img");
        photo.className = "hana-bubble__photo";
        photo.src = safe;
        photo.alt = "";
        photo.setAttribute("aria-hidden", "true");
        photo.addEventListener("error", () => {
          photo.style.display = "none";
        });
        this.element.appendChild(photo);
      }
    }

    // Bubble content
    const bubble = document.createElement("div");
    bubble.className = "hana-bubble__content";

    if (isUser || isAgent) {
      // User + live-agent messages: plain text only. Agent text is
      // human-typed; rendering it as plain text (not markdown) avoids
      // accidental formatting and keeps the agent's words verbatim.
      bubble.textContent = message.content ?? "";
    } else {
      // Bot messages: markdown rendered with DOMPurify sanitization
      bubble.innerHTML = DOMPurify.sanitize(
        parseMarkdown(message.content ?? ""),
        {
          ALLOWED_TAGS: [
            "p",
            "br",
            "strong",
            "em",
            "a",
            "ul",
            "ol",
            "li",
            "code",
            "pre",
            "span",
          ],
          ALLOWED_ATTR: ["href", "target", "rel", "class"],
          ALLOW_DATA_ATTR: false,
        },
      );
    }

    this.element.appendChild(bubble);

    // Footer row: timestamp + status
    const footer = document.createElement("div");
    footer.className = "hana-bubble__footer";

    const timeEl = document.createElement("span");
    timeEl.className = "hana-bubble__time";
    // Stamp the message's epoch ms onto the element so the periodic
    // ticker in MessageList can recompute "Just now" → "5 min ago"
    // → "1 hr ago" without re-rendering the entire bubble.
    timeEl.setAttribute("data-message-timestamp", String(message.timestamp));
    timeEl.textContent = formatTimestamp(message.timestamp);
    footer.appendChild(timeEl);

    // Status indicator for user messages
    if (isUser && message.status) {
      footer.appendChild(createStatusIndicator(message.status));
    }

    this.element.appendChild(footer);
  }

  getElement(): HTMLElement {
    return this.element;
  }
}
